import { useState, useEffect, useCallback, useRef } from 'react';
import { Database, ChevronLeft, ChevronRight, Search, RefreshCw, Table2, AlertCircle, Link2, X, ArrowRight, MapPin, Truck, Package, GitBranch, Shield, Eye, Copy, Check, Code2, Download } from 'lucide-react';
import DataTable from '../DataTable';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import OrderInvestigationTimeline from '../orderInvestigation/OrderInvestigationTimeline';
import VehicleWindowProjectionView from '../vehicleInvestigation/VehicleWindowProjectionView';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface TableInfo {
  key: string;
  name: string;
  table: string;
}

interface TableStats {
  name: string;
  count: number;
  error?: string;
}

function statErrorLabel(error: string): string {
  switch (error) {
    case 'stats_deferred': return 'Record count unavailable — the count query timed out. Use refresh to retry.';
    case 'table_not_found': return 'Table does not exist in this environment yet.';
    case 'count_failed': return 'Record count unavailable — the count query failed.';
    default: return `Record count unavailable (${error}).`;
  }
}

/** An entry of a payload's `_observed_identifiers` list. */
interface ObservedIdentifier {
  id_type: string;
  id_value: string;
}

/**
 * A truth-layer record as returned by the API. Tables differ in their columns,
 * so the event fields the browser renders are declared and any extra column is
 * still allowed.
 */
interface TruthRecord extends Record<string, unknown> {
  event_type: string;
  occurred_at: string;
  received_at: string;
  source_system: string;
}

interface QueryResult {
  columns: string[];
  data: Record<string, unknown>[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface RelatedRecords {
  entityId: string;
  sourceTable: string;
  sourceTableName: string;
  sourceRecord: TruthRecord | null;
  relatedRecords: Record<string, {
    name: string;
    description: string;
    count: number;
    records: TruthRecord[];
  }>;
}

interface GraphEdgeResult {
  relationship_type: string;
  edge_id: string;
  from_entity_id: string;
  from_entity_type: string;
  to_entity_id: string;
  to_entity_type: string;
  from_ts: string;
  to_ts: string;
  confidence: number;
  authority_level: string;
  derivation: string;
  evidence_refs: string[];
  ingested_at: string;
  properties?: Record<string, unknown>;
}

interface GraphRelationshipsResponse {
  entityId: string;
  entityType: string;
  organisationId: string;
  window: { from: string; to: string };
  totalEdges: number;
  byType: Record<string, GraphEdgeResult[]>;
  edges: GraphEdgeResult[];
  entityLabels?: Record<string, string>;
  entityLabelTypes?: Record<string, string>;
  timestamp: string;
}

// Get icon for table type
function getTableIcon(tableKey: string) {
  switch (tableKey) {
    case 'order_observed':
      return <Package className="w-4 h-4" />;
    case 'order_stop_declared':
      return <MapPin className="w-4 h-4" />;
    case 'movement_leg_declared':
      return <Truck className="w-4 h-4" />;
    default:
      return <Table2 className="w-4 h-4" />;
  }
}

export default function TruthBrowser() {
  // State
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [stats, setStats] = useState<Record<string, TableStats>>({});
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [data, setData] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination & filters
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // View mode: 'canonical' (deduped by registration/VIN) or 'claims' (all observed claims)
  const [viewMode, setViewMode] = useState<'canonical' | 'claims'>('canonical');
  // Relationship browser state
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [selectedEntityLabel, setSelectedEntityLabel] = useState<{ type: string; value: string } | null>(null);
  const [relatedData, setRelatedData] = useState<RelatedRecords | null>(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [showRelationships, setShowRelationships] = useState(false);

  // Graph relationships state
  const [graphData, setGraphData] = useState<GraphRelationshipsResponse | null>(null);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [relationshipTab, setRelationshipTab] = useState<'records' | 'graph' | 'investigation'>('records');
  const [vehicleInvestigationDate, setVehicleInvestigationDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Full record modal state
  const [fullRecordModal, setFullRecordModal] = useState<{ title: string; record: Record<string, unknown> } | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // When navigating from an external vehicle link, auto-open Relationship Explorer for first matched row
  const pendingAutoSelectRef = useRef(false);

  // Load available tables and stats on mount (independently — stats can be slow/timeout on Trino)
  useEffect(() => {
    let cancelled = false;
    const TABLES_TIMEOUT_MS = 15_000;
    let tablesTimeoutId: number | undefined;

    async function loadTables() {
      tablesTimeoutId = window.setTimeout(() => {
        if (cancelled) return;
        setTables((current) => {
          if (current.length > 0) return current;
          setError((prev) => prev ?? 'Timed out loading tables. Is zeecore running on :4010?');
          return current;
        });
      }, TABLES_TIMEOUT_MS);

      try {
        const tablesRes = await authenticatedFetch(API_ENDPOINTS.truthBrowser.tables());
        if (cancelled) return;
        if (tablesRes.ok) {
          const tablesData = await tablesRes.json();
          setTables(tablesData.tables || []);
          setError(null);
        } else {
          setError(`Failed to load tables (HTTP ${tablesRes.status}). Is zeecore running on :4010?`);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load truth browser tables:', err);
        setError('Failed to connect to truth layer. Is the server running?');
      } finally {
        if (tablesTimeoutId != null) window.clearTimeout(tablesTimeoutId);
      }
    }

    async function loadStats() {
      try {
        const statsRes = await authenticatedFetch(API_ENDPOINTS.truthBrowser.stats());
        if (cancelled) return;
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData.stats || {});
        }
      } catch (err) {
        // Stats are optional badges — don't block the table list
        console.error('Failed to load truth browser stats:', err);
      }
    }

    loadTables();
    loadStats();
    return () => {
      cancelled = true;
      if (tablesTimeoutId != null) window.clearTimeout(tablesTimeoutId);
    };
  }, []);

  // Force-refresh stats (bypasses server cache)
  const refreshStats = useCallback(async () => {
    try {
      const statsRes = await authenticatedFetch(API_ENDPOINTS.truthBrowser.stats({ refresh: true }));
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats || {});
      }
    } catch (err) {
      console.error('Failed to refresh stats:', err);
    }
  }, []);

  // Navigate to a specific vehicle when triggered from another tab (e.g. Profitability Report)
  useEffect(() => {
    const handler = (e: CustomEvent<{ vehicleId: string; vehicleEntityId?: string }>) => {
      const vehicleId = e.detail?.vehicleId;
      const vehicleEntityId = e.detail?.vehicleEntityId;
      if (!vehicleId) return;
      setSelectedTable('vehicle_observed');
      setPage(1);
      setSearch(vehicleId);
      setSearchInput(vehicleId);
      setRelatedData(null);
      setGraphData(null);
      setRelationshipTab('graph');

      if (vehicleEntityId) {
        // We have the entity ID directly — open Relationship Explorer immediately
        setSelectedEntityId(vehicleEntityId);
        setSelectedEntityLabel({ type: 'Registration', value: vehicleId });
        setShowRelationships(true);
        pendingAutoSelectRef.current = false;
      } else {
        // Fall back to searching and auto-selecting the first result
        setSelectedEntityId(null);
        setSelectedEntityLabel(null);
        setShowRelationships(false);
        pendingAutoSelectRef.current = true;
      }
    };
    window.addEventListener('navigate-to-truth-browser', handler as EventListener);
    return () => window.removeEventListener('navigate-to-truth-browser', handler as EventListener);
  }, []);

  // Load data when table or filters change
  const loadData = useCallback(async () => {
    if (!selectedTable) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.truthBrowser.data(selectedTable, {
          page,
          pageSize,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          search: search || undefined,
          sortBy: 'occurred_at',
          sortOrder: 'desc',
          view: viewMode,
        })
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load data');
      }

      const result = await response.json();
      setData(result);
      // If navigated from a vehicle link, auto-open the Relationship Explorer for the first match
      if (pendingAutoSelectRef.current && result.data?.length > 0) {
        pendingAutoSelectRef.current = false;
        const firstRow = result.data[0];
        const entityId: string = firstRow.subject_entity_id;
        if (entityId) {
          setSelectedEntityId(entityId);
          const label = firstRow.payload?.registration
            ? { type: 'Registration', value: String(firstRow.payload.registration) }
            : firstRow.payload?.vin
            ? { type: 'VIN', value: String(firstRow.payload.vin) }
            : null;
          setSelectedEntityLabel(label);
          setShowRelationships(true);
          setRelationshipTab('graph');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedTable, page, pageSize, fromDate, toDate, search, viewMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // When a vehicle entity ID is set directly (via external link) and relationships panel is open, load them
  useEffect(() => {
    if (selectedEntityId && showRelationships && !relatedData && !graphData) {
      loadRelatedRecords(selectedEntityId);
      loadGraphRelationships(selectedEntityId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityId, showRelationships]);

  // Load related records when entity is selected
  const loadRelatedRecords = useCallback(async (entityId: string) => {
    if (!selectedTable) return;

    setLoadingRelated(true);
    setShowRelationships(true);
    setRelationshipTab('records');

    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.truthBrowser.related(selectedTable, entityId)
      );

      if (!response.ok) {
        throw new Error('Failed to load related records');
      }

      const result = await response.json();
      setRelatedData(result);
    } catch (err) {
      console.error('Failed to load related records:', err);
      setRelatedData(null);
    } finally {
      setLoadingRelated(false);
    }
  }, [selectedTable]);

  // Load graph relationships when entity is selected
  const loadGraphRelationships = useCallback(async (entityId: string) => {
    if (!selectedTable) return;

    // Derive entity type from table key
    const entityTypeMap: Record<string, string> = {
      order_observed: 'order',
      order_stop_declared: 'order',
      vehicle_observed: 'vehicle',
      vehicle_position_observed: 'vehicle',
      driver_observed: 'driver',
      driver_shift_observed: 'driver',
      fuel_transaction_observed: 'fuel_transaction',
      fuel_transaction_declared: 'fuel_transaction',
      movement_leg_declared: 'vehicle',
      movement_leg_inferred: 'vehicle',
    };
    const entityType = entityTypeMap[selectedTable] || 'unknown';

    // Use date filters if available, otherwise default to last 90 days
    const now = new Date();
    const defaultFrom = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
    const defaultTo = now.toISOString();

    setLoadingGraph(true);
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.truthBrowser.graphRelationships(entityType, entityId, {
          from: fromDate || defaultFrom,
          to: toDate || defaultTo,
        })
      );

      if (!response.ok) {
        throw new Error('Failed to load graph relationships');
      }

      const result = await response.json();
      setGraphData(result);
      // Update label from backend resolution if we don't already have one
      if (result.entityLabels?.[entityId] && !selectedEntityLabel) {
        const labelType = result.entityLabelTypes?.[entityId] || 'Identifier';
        setSelectedEntityLabel({ type: labelType, value: result.entityLabels[entityId] });
      }
    } catch (err) {
      console.error('Failed to load graph relationships:', err);
      setGraphData(null);
    } finally {
      setLoadingGraph(false);
    }
  }, [selectedTable, fromDate, toDate]);

  /**
   * Extract a human-readable label from a row's payload.
   * Returns { type, value } where type is the label kind (e.g. "Registration", "Order Ref").
   */
  const extractLabelFromPayload = (payload: unknown): { type: string; value: string } | null => {
    if (!payload || typeof payload !== 'object') return null;
    const record = payload as Record<string, unknown>;
    // Vehicle registration
    if (record.registration) return { type: 'Registration', value: String(record.registration) };
    // Order name / reference
    if (record.name) return { type: 'Name', value: String(record.name) };
    if (record.order_id) return { type: 'Order Ref', value: String(record.order_id) };
    if (record.customer_reference) return { type: 'Customer Ref', value: String(record.customer_reference) };
    // Driver
    if (record.driver_id) return { type: 'Driver ID', value: String(record.driver_id) };
    // Fuel transaction
    if (record.transaction_id) return { type: 'Transaction ID', value: String(record.transaction_id) };
    if (record.station_name) return { type: 'Station', value: String(record.station_name) };
    // VIN as last resort
    if (record.vin) return { type: 'VIN', value: String(record.vin) };
    // Check _observed_identifiers for a VRN / registration
    const idents = record._observed_identifiers;
    if (Array.isArray(idents)) {
      const identifiers = idents as ObservedIdentifier[];
      const vrn = identifiers.find(
        (id) => id.id_type === 'registration' || id.id_type === 'vrn' || id.id_type === 'vehicle_registration',
      );
      if (vrn?.id_value) return { type: 'Registration', value: String(vrn.id_value) };
      const firstUseful = identifiers.find((id) => id.id_value && id.id_type !== 'entity_id');
      if (firstUseful?.id_value) return { type: firstUseful.id_type.replace(/_/g, ' '), value: String(firstUseful.id_value) };
    }
    return null;
  };

  // Handle clicking on a row to view relationships
  const handleRowClick = (row: Record<string, unknown>) => {
    // For movement_leg_declared, use event_id to look up the movement itself
    // For other tables, use subject_entity_id to find related records
    let entityId: unknown;
    
    if (selectedTable === 'movement_leg_declared' && row.event_id) {
      // Use event_id for movements so we can find the specific movement record
      // and extract its related_order_entity_ids
      entityId = row.event_id;
    } else {
      // Use subject_entity_id for orders and other tables
      entityId = row.subject_entity_id;
    }
    
    if (entityId && typeof entityId === 'string') {
      setSelectedEntityId(entityId);
      // Try to extract a human-readable label from the row payload immediately
      const label = extractLabelFromPayload(row.payload);
      setSelectedEntityLabel(label);
      loadRelatedRecords(entityId);
      loadGraphRelationships(entityId);
    }
  };

  // Handle search submit
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  // Handle table selection
  const handleTableSelect = (tableKey: string) => {
    setSelectedTable(tableKey);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setShowRelationships(false);
    setRelatedData(null);
    setSelectedEntityId(null);
    setSelectedEntityLabel(null);
    setGraphData(null);
    setRelationshipTab('records');
  };

  // Download the currently viewed Relationship Explorer as a JSON file
  const downloadRelationshipExplorer = useCallback(() => {
    const payload = {
      exportedAt: new Date().toISOString(),
      entityId: selectedEntityId,
      entityLabel: selectedEntityLabel,
      relatedRecords: relatedData ?? null,
      graphRelationships: graphData ?? null,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const labelPart = selectedEntityLabel?.value
      ? selectedEntityLabel.value.replace(/[^a-zA-Z0-9_-]/g, '_')
      : selectedEntityId ?? 'entity';
    a.download = `relationship-explorer_${labelPart}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedEntityId, selectedEntityLabel, relatedData, graphData]);

  // Navigate to a counterpart entity by loading its relationships directly
  const navigateToCounterpart = (entityId: string, entityType: string) => {
    // Map entity type back to a canonical table so loadGraphRelationships works
    const typeToTable: Record<string, string> = {
      order: 'order_observed',
      vehicle: 'vehicle_observed',
      driver: 'driver_observed',
      fuel: 'fuel_transaction_observed',
    };
    const tableKey = typeToTable[entityType] || selectedTable || 'order_observed';
    if (tableKey !== selectedTable) {
      setSelectedTable(tableKey);
      setPage(1);
      setSearch('');
      setSearchInput('');
      setData(null);
    }
    setSelectedEntityId(entityId);
    // Carry forward label from graphData if we already resolved it
    const existingValue = graphData?.entityLabels?.[entityId];
    const existingType = graphData?.entityLabelTypes?.[entityId] || 'Identifier';
    setSelectedEntityLabel(existingValue ? { type: existingType, value: existingValue } : null);
    setShowRelationships(true);
    setRelationshipTab('graph');
    setRelatedData(null);
    setGraphData(null);
    loadRelatedRecords(entityId);
    loadGraphRelationships(entityId);
  };

  // Close relationship panel
  const closeRelationships = () => {
    setShowRelationships(false);
    setRelatedData(null);
    setSelectedEntityId(null);
    setSelectedEntityLabel(null);
    setGraphData(null);
    setRelationshipTab('records');
  };

  // Copy full record JSON to clipboard
  const copyRecordJson = (record: Record<string, unknown>) => {
    navigator.clipboard.writeText(JSON.stringify(record, null, 2)).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // Format payload for relationship view (more readable)
  const formatPayloadForRelationship = (payload: unknown): React.ReactNode => {
    if (!payload || typeof payload !== 'object') return null;

    const entries = Object.entries(payload);
    if (entries.length === 0) return <span className="text-muted-foreground">Empty payload</span>;

    return (
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {entries.map(([key, value]) => (
          <div key={key} className="flex gap-2 min-w-0">
            <span className="text-muted-foreground font-medium shrink-0">{key}:</span>
            <span className="text-foreground break-all">
              {typeof value === 'object'
                ? <details className="cursor-pointer inline">
                    <summary className="text-ring hover:text-ring list-none inline">[object]</summary>
                    <pre className="mt-1 p-1 bg-background rounded text-xs overflow-auto max-h-40 whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
                  </details>
                : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Format cell value for display
  const formatCellValue = (value: unknown, columnKey: string): string | React.ReactNode => {
    if (value === null || value === undefined) return '-';
    
    // Format dates
    if (columnKey === 'occurred_at' || columnKey === 'received_at') {
      try {
        const date = new Date(value as string | number);
        return date.toLocaleString();
      } catch {
        return String(value);
      }
    }
    
    // Format payload as expandable JSON with View Full button
    if (columnKey === 'payload') {
      if (typeof value === 'object') {
        const jsonStr = JSON.stringify(value, null, 2);
        const preview = JSON.stringify(value).substring(0, 80);
        return (
          <details className="cursor-pointer">
            <summary className="text-ring hover:text-ring text-xs">
              {preview.length < JSON.stringify(value).length ? preview + '...' : preview}
            </summary>
            <div className="mt-2">
              <pre className="p-2 bg-background rounded text-xs overflow-auto max-h-48 whitespace-pre-wrap">{jsonStr}</pre>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setFullRecordModal({ title: 'Payload', record: value as Record<string, unknown> }); }}
                className="mt-1 h-auto gap-1 px-0 text-xs text-success hover:bg-transparent"
              >
                <Eye className="w-3 h-3" /> View full
              </Button>
            </div>
          </details>
        );
      }
      return String(value).substring(0, 100);
    }
    
    // For entity IDs and other long strings: show truncated with tooltip
    if (typeof value === 'string' && value.length > 50) {
      return (
        <span title={value} className="cursor-help">
          {value.substring(0, 47)}...
        </span>
      );
    }
    
    return String(value);
  };

  // Prepare columns for DataTable
  const columns = data?.columns
    .filter(col => col !== 'payload') // Move payload to end
    .concat(['payload'])
    .map(col => ({
      key: col,
      label: col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      align: col === 'payload' ? 'left' as const : undefined,
    })) || [];

  // Prepare rows for DataTable
  const rows = data?.data.map((row, idx) => {
    const formattedRow: Record<string, unknown> = { 
      _id: idx,
      subject_entity_id: row.subject_entity_id, // Keep raw value for click handling
    };
    for (const col of data.columns) {
      formattedRow[col] = formatCellValue(row[col], col);
    }
    return formattedRow;
  }) || [];

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6 text-success" />
          <div>
            <h3 className="text-lg font-semibold text-white">Truth Layer Browser</h3>
            <p className="text-muted-foreground text-xs">Explore data and relationships in the truth layer</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showRelationships && (
            <Button
              variant="secondary"
              size="sm"
              onClick={closeRelationships}
              className="h-auto rounded-md px-3 py-1.5 text-sm font-medium"
            >
              <X className="w-4 h-4" />
              Close Relationships
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            loading={loading}
            onClick={() => { loadData(); refreshStats(); }}
            disabled={loading || !selectedTable}
            className="h-auto rounded-md px-3 py-1.5 text-sm font-medium"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Table Selection */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tables.map(table => {
          const stat = stats[table.key];
          const isSelected = selectedTable === table.key;
          return (
            <Button
              key={table.key}
              variant={isSelected ? 'default' : 'secondary'}
              size="sm"
              onClick={() => handleTableSelect(table.key)}
              className={cn(
                'h-auto rounded-lg px-3 py-2 text-sm font-medium',
                isSelected ? 'shadow-lg shadow-success/30' : '',
              )}
            >
              {getTableIcon(table.key)}
              <span>{table.name}</span>
              {stat && (
                <span
                  className={`px-1.5 py-0.5 rounded text-xs ${
                    isSelected ? 'bg-primary' : 'bg-accent'
                  }`}
                  title={stat.error ? statErrorLabel(stat.error) : undefined}
                >
                  {/* A failed/timed-out count is unknown, not zero. */}
                  {stat.error ? '—' : stat.count.toLocaleString()}
                </span>
              )}
            </Button>
          );
        })}
      </div>

      {/* Hint about clicking rows */}
      {selectedTable && !showRelationships && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-ring/20 border border-ring/30 rounded-lg text-sm text-ring">
          <Link2 className="w-4 h-4" />
          <span>Click on any row to explore relationships and see the full history of that record</span>
        </div>
      )}

      {/* Filters */}
      {selectedTable && !showRelationships && (
        <div className="flex flex-wrap gap-3 mb-4 p-3 bg-background/50 rounded-lg">
          {/* Search */}
          <div className="flex items-center gap-2 flex-1 min-w-50">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search in payload, entity ID, source..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="flex-1 bg-card border border-border rounded px-3 py-1.5 text-sm text-white placeholder-muted-foreground focus:outline-none focus:border-ring"
            />
            <Button
              variant="default"
              size="sm"
              onClick={handleSearch}
              className="h-auto rounded px-3 py-1.5 text-sm"
            >
              Search
            </Button>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">From:</span>
            <input
              type="date"
              value={fromDate}
              onChange={e => { setFromDate(e.target.value); setPage(1); }}
              className="bg-card border border-border rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-ring"
            />
            <span className="text-muted-foreground text-sm">To:</span>
            <input
              type="date"
              value={toDate}
              onChange={e => { setToDate(e.target.value); setPage(1); }}
              className="bg-card border border-border rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-ring"
            />
          </div>

          {/* Page Size */}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Show:</span>
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="bg-card border border-border rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-ring"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* View Mode Toggle — Constitution §3.2: Observed Claims vs Canonical Selected */}
          {selectedTable === 'vehicle_observed' && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">View:</span>
              <Button
                variant={viewMode === 'canonical' ? 'default' : 'secondary'}
                size="sm"
                onClick={() => { setViewMode('canonical'); setPage(1); }}
                className={cn(
                  'h-auto rounded px-2 py-1 text-xs font-medium',
                  viewMode !== 'canonical' ? 'text-muted-foreground hover:text-foreground' : '',
                )}
              >
                Canonical
              </Button>
              <Button
                variant={viewMode === 'claims' ? 'warning' : 'secondary'}
                size="sm"
                onClick={() => { setViewMode('claims'); setPage(1); }}
                className={cn(
                  'h-auto rounded px-2 py-1 text-xs font-medium',
                  viewMode !== 'claims' ? 'text-muted-foreground hover:text-foreground' : '',
                )}
              >
                <Eye className="w-3 h-3 inline mr-1" />
                All Claims
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-destructive/20 border border-destructive/30 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 text-destructive" />
          <span className="text-destructive">{error}</span>
        </div>
      )}

      {/* Empty State */}
      {!selectedTable && !error && tables.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
          <p className="text-lg">Loading tables…</p>
          <p className="text-sm mt-1">Fetching the truth layer table list</p>
        </div>
      )}
      {!selectedTable && !error && tables.length > 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg">Select a table above to browse data</p>
          <p className="text-sm mt-1">View orders, vehicle positions, movements, and more</p>
        </div>
      )}

      {/* Loading State */}
      {loading && selectedTable && !showRelationships && (
        <div className="text-center py-12 text-muted-foreground">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin" />
          <p>Loading data...</p>
        </div>
      )}

      {/* Relationship Browser Panel */}
      {showRelationships && selectedTable && (
        <div className="bg-background rounded-lg border border-success/50 p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-success" />
              <div>
                <h4 className="text-white font-medium">Relationship Explorer</h4>
                <p className="text-muted-foreground text-xs">Entity ID: {selectedEntityId}</p>
                {selectedEntityLabel && (
                  <p className="text-success text-xs font-medium">
                    {selectedEntityLabel.type}: {selectedEntityLabel.value}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={downloadRelationshipExplorer}
                title="Download as JSON"
                className="h-auto rounded px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <Download className="w-3.5 h-3.5" />
                Export JSON
              </Button>
              <Button
                variant="ghost"
                size="iconSm"
                onClick={closeRelationships}
                className="rounded hover:bg-card"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </Button>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex gap-1 mb-4 bg-card rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRelationshipTab('records')}
              className={cn(
                'h-auto rounded px-3 py-1.5 text-sm',
                relationshipTab === 'records'
                  ? 'bg-success/30 text-success hover:bg-success/30'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
              )}
            >
              <Link2 className="w-4 h-4" />
              Related Records
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRelationshipTab('graph')}
              className={cn(
                'h-auto rounded px-3 py-1.5 text-sm',
                relationshipTab === 'graph'
                  ? 'bg-chart-5/30 text-chart-5 hover:bg-chart-5/30'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
              )}
            >
              <GitBranch className="w-4 h-4" />
              Graph Relationships
              {graphData && graphData.totalEdges > 0 && (
                <Badge className="bg-chart-5/40 text-chart-5">
                  {graphData.totalEdges}
                </Badge>
              )}
            </Button>
            {['order_observed', 'order_stop_declared',
              'vehicle_observed', 'vehicle_position_observed',
              'movement_leg_declared', 'movement_leg_inferred'].includes(selectedTable || '') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRelationshipTab('investigation')}
                className={cn(
                  'h-auto rounded px-3 py-1.5 text-sm',
                  relationshipTab === 'investigation'
                    ? 'bg-chart-2/30 text-chart-2 hover:bg-chart-2/30'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                )}
              >
                <Search className="w-4 h-4" />
                Investigation
              </Button>
            )}
          </div>

          {/* Tab Content: Related Records */}
          {relationshipTab === 'records' && (
            <>
          {loadingRelated ? (
            <div className="text-center py-8 text-muted-foreground">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
              <p className="text-sm">Loading related records...</p>
            </div>
          ) : relatedData ? (
            <div className="space-y-4">
              {/* Bi-temporal Legend */}
              <div className="flex items-center gap-4 px-3 py-2 bg-card/50 rounded text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-success"></span>
                  <strong>Business Time</strong> (occurred_at): When it happened in reality
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-ring"></span>
                  <strong>System Time</strong> (received_at): When we learned about it
                </span>
              </div>

              {/* Source Record */}
              {relatedData.sourceRecord && (
                <div className="bg-card rounded-lg p-3 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getTableIcon(relatedData.sourceTable)}
                      <span className="text-success font-medium">{relatedData.sourceTableName}</span>
                      <span className="text-muted-foreground text-xs">(Source Record)</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFullRecordModal({ title: `${relatedData.sourceTableName} — Full Record`, record: relatedData.sourceRecord! })}
                      className="h-auto rounded border border-success/30 bg-success/30 px-2 py-1 text-xs text-success hover:bg-success/50"
                    >
                      <Eye className="w-3 h-3" /> View Full Object
                    </Button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs mb-2">
                    <div>
                      <span className="text-muted-foreground">Event Type:</span>{' '}
                      <span className="text-foreground">{relatedData.sourceRecord.event_type}</span>
                    </div>
                    <div title="Business Time: When the event actually occurred">
                      <span className="text-success">⏱ Business:</span>{' '}
                      <span className="text-foreground">
                        {new Date(relatedData.sourceRecord.occurred_at).toLocaleString()}
                      </span>
                    </div>
                    <div title="System Time: When we received/ingested this data">
                      <span className="text-ring">📥 Received:</span>{' '}
                      <span className="text-foreground">
                        {new Date(relatedData.sourceRecord.received_at).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Source:</span>{' '}
                      <span className="text-foreground">{relatedData.sourceRecord.source_system}</span>
                    </div>
                  </div>
                  {Boolean(relatedData.sourceRecord.payload) && (
                    <div className="bg-background rounded p-2 max-h-64 overflow-y-auto">
                      {formatPayloadForRelationship(relatedData.sourceRecord.payload)}
                    </div>
                  )}
                </div>
              )}

              {/* Related Records */}
              {Object.entries(relatedData.relatedRecords).map(([tableKey, rel]) => (
                <div key={tableKey} className="bg-card rounded-lg border border-border overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      {getTableIcon(tableKey)}
                      <span className="text-white font-medium">{rel.name}</span>
                      <span className="text-muted-foreground text-xs">- {rel.description}</span>
                    </div>
                    <Badge tone={rel.count > 0 ? 'success' : 'neutral'}>
                      {rel.count} record{rel.count !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  
                  {rel.count > 0 ? (
                    <div className="divide-y divide-border max-h-64 overflow-y-auto">
                      {rel.records.map((record, idx) => (
                        <div key={idx} className="p-3 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3 text-xs">
                              <span className="text-muted-foreground">#{idx + 1}</span>
                              <span className="text-chart-5">{record.event_type}</span>
                              <span className="text-success" title="Business Time">
                                ⏱ {new Date(record.occurred_at).toLocaleString()}
                              </span>
                              {record.occurred_at !== record.received_at && (
                                <span className="text-ring" title="System Time (when received)">
                                  📥 {new Date(record.received_at).toLocaleString()}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{record.source_system}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFullRecordModal({ title: `${record.event_type} — Full Record`, record })}
                                className="h-auto rounded border border-success/30 bg-success/30 px-1.5 py-0.5 text-xs text-success hover:bg-success/50"
                              >
                                <Eye className="w-3 h-3" /> View
                              </Button>
                            </div>
                          </div>
                          {Boolean(record.payload) && (
                            <div className="bg-background rounded p-2 max-h-48 overflow-y-auto">
                              {formatPayloadForRelationship(record.payload)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No related records found
                    </div>
                  )}
                </div>
              ))}

              {/* No relationships defined */}
              {Object.keys(relatedData.relatedRecords).length === 0 && (
                <div className="text-center py-6 text-muted-foreground">
                  <Link2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No relationships defined for this table type</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-6 h-6 mx-auto mb-2" />
              <p className="text-sm">Failed to load related records</p>
            </div>
          )}
            </>
          )}

          {/* Tab Content: Graph Relationships */}
          {relationshipTab === 'graph' && (
            <>
              {loadingGraph ? (
                <div className="text-center py-8 text-muted-foreground">
                  <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">Loading graph relationships...</p>
                </div>
              ) : graphData && graphData.totalEdges > 0 ? (
                <div className="space-y-4">
                  {/* Summary bar */}
                  <div className="flex items-center gap-4 px-3 py-2 bg-card/50 rounded text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <GitBranch className="w-3 h-3 text-chart-5" />
                      <strong>{graphData.totalEdges}</strong> resolved edge{graphData.totalEdges !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-warning" />
                      Authority: Decision {'>'} Observed {'>'} Declared {'>'} Inferred
                    </span>
                  </div>

                  {/* Edges by relationship type */}
                  {Object.entries(graphData.byType).map(([relType, edges]) => (
                    <div key={relType} className="bg-card rounded-lg border border-border overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                        <div className="flex items-center gap-2">
                          <GitBranch className="w-4 h-4 text-chart-5" />
                          <span className="text-white font-medium">
                            {relType.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <Badge tone="brand">
                          {edges.length} edge{edges.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>

                      <div className="divide-y divide-border max-h-80 overflow-y-auto">
                        {edges.map((edge, idx) => {
                          // Authority levels — Observed/Declared/Inferred are the
                          // provenance tiers (Constitution §13.2): green/blue/violet,
                          // via `Badge`'s `provenance` prop from the shared colors
                          // module. Decision is a status role, not a tier.
                          const authorityTiers: Record<string, 'observed' | 'declared' | 'inferred'> = {
                            Observed: 'observed',
                            Declared: 'declared',
                            Inferred: 'inferred',
                          };
                          const authorityTier = authorityTiers[edge.authority_level] ?? 'inferred';
                          const confidencePct = Math.round(edge.confidence * 100);

                          // Determine counterpart entity
                          const isFrom = edge.from_entity_id === graphData.entityId;
                          const counterpartId = isFrom ? edge.to_entity_id : edge.from_entity_id;
                          const counterpartType = isFrom ? edge.to_entity_type : edge.from_entity_type;

                          return (
                            <div key={edge.edge_id} className="p-3 transition-colors">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="text-muted-foreground">#{idx + 1}</span>
                                  <Badge
                                    tone={edge.authority_level === 'Decision' ? 'success' : 'neutral'}
                                    provenance={edge.authority_level === 'Decision' ? undefined : authorityTier}
                                  >
                                    {edge.authority_level}
                                  </Badge>
                                  <span className="text-muted-foreground">
                                    {edge.derivation?.replace(/_/g, ' ')}
                                  </span>
                                  {typeof edge.properties?.leg_role === 'string' && edge.properties.leg_role && (
                                    <Badge
                                      tone={
                                        edge.properties.leg_role === 'delivery'
                                          ? 'warning'
                                          : edge.properties.leg_role === 'collection'
                                            ? 'success'
                                            : 'neutral'
                                      }
                                      title="Carrying leg role derived from association timeline"
                                    >
                                      {String(edge.properties.leg_role).replace(/_/g, ' ')}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Confidence bar */}
                                  <div className="flex items-center gap-1">
                                    <div className="w-16 h-1.5 bg-secondary rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${
                                          confidencePct >= 80 ? 'bg-success' :
                                          confidencePct >= 50 ? 'bg-warning' : 'bg-destructive'
                                        }`}
                                        style={{ width: `${confidencePct}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-muted-foreground">{confidencePct}%</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-col gap-1.5 text-xs">
                                <div>
                                  <span className="text-muted-foreground">Counterpart:</span>{' '}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigateToCounterpart(counterpartId, counterpartType)}
                                    className="h-auto w-full flex-col items-start justify-start gap-0 rounded px-0 text-left font-mono text-xs text-chart-5 hover:bg-transparent hover:underline"
                                    title={`Browse ${counterpartType}: ${counterpartId}`}
                                  >
                                    {graphData?.entityLabels?.[counterpartId] ? (
                                      <span className="flex flex-col gap-0.5 text-left">
                                        <span className="text-chart-5 font-sans font-medium">
                                          {graphData.entityLabelTypes?.[counterpartId] || counterpartType}: {graphData.entityLabels[counterpartId]}
                                        </span>
                                        <span className="text-muted-foreground font-mono text-xs">
                                          Entity ID: {counterpartId}
                                        </span>
                                      </span>
                                    ) : (
                                      <>{counterpartType}: {counterpartId}</>
                                    )}
                                  </Button>
                                </div>
                                {!!edge.properties?.resource_type && (
                                  <div>
                                    <span className="text-muted-foreground">Resource type:</span>{' '}
                                    <span className="text-foreground font-mono">{String(edge.properties.resource_type)}</span>
                                  </div>
                                )}
                                {typeof edge.properties?.depot === 'string' && edge.properties.depot && (
                                  <div>
                                    <span className="text-muted-foreground">Depot:</span>{' '}
                                    <span className="text-success font-mono">{String(edge.properties.depot)}</span>
                                  </div>
                                )}
                                {Array.isArray(edge.properties?.association_kinds) && edge.properties.association_kinds.length > 0 && (
                                  <div>
                                    <span className="text-muted-foreground">Association:</span>{' '}
                                    <span className="text-foreground">
                                      {(edge.properties.association_kinds as unknown[]).map(String).join(', ')}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-muted-foreground">Window:</span>{' '}
                                  <span className="text-foreground tabular-nums">
                                    {new Date(edge.from_ts).toLocaleString('en-GB', {
                                      timeZone: 'Europe/London',
                                      dateStyle: 'short',
                                      timeStyle: 'short',
                                    })}
                                    {' → '}
                                    {edge.to_ts
                                      ? new Date(edge.to_ts).toLocaleString('en-GB', {
                                          timeZone: 'Europe/London',
                                          dateStyle: 'short',
                                          timeStyle: 'short',
                                        })
                                      : 'open'}
                                    <span className="ml-1 text-xs uppercase text-muted-foreground">UK</span>
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Evidence:</span>{' '}
                                  <span className="text-foreground">
                                    {edge.evidence_refs?.length || 0} ref{(edge.evidence_refs?.length || 0) !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : graphData && graphData.totalEdges === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No graph edges found for this entity</p>
                  <p className="text-xs mt-1 text-muted-foreground">Edges are derived from canonical events. Ensure the graph derivation pipeline has processed this entity.</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-sm">Failed to load graph relationships</p>
                </div>
              )}
            </>
          )}

          {/* Tab Content: Investigation (Order or Vehicle) */}
          {relationshipTab === 'investigation' && selectedEntityId && (
            ['order_observed', 'order_stop_declared'].includes(selectedTable || '')
              ? (
                <OrderInvestigationTimeline
                  orderId={selectedEntityId}
                  displayName={selectedEntityLabel?.value}
                />
              )
              : ['vehicle_observed', 'vehicle_position_observed',
                  'movement_leg_declared', 'movement_leg_inferred'].includes(selectedTable || '')
              ? (
                <div className="space-y-4">
                  {/* Date picker for vehicle investigation */}
                  <div className="flex items-center gap-3 bg-card/50 rounded-lg px-4 py-2 border border-border/50">
                    <label className="text-sm text-muted-foreground">Operating Date</label>
                    <input
                      type="date"
                      value={vehicleInvestigationDate}
                      onChange={(e) => setVehicleInvestigationDate(e.target.value)}
                      className="bg-secondary text-white text-sm rounded px-3 py-1.5 border border-border focus:border-warning focus:outline-none"
                    />
                  </div>
                  <VehicleWindowProjectionView
                    vehicleId={selectedEntityId}
                    date={vehicleInvestigationDate}
                    displayName={selectedEntityLabel?.value}
                  />
                </div>
              )
              : null
          )}
        </div>
      )}

      {/* Data Table */}
      {!loading && data && data.data.length > 0 && !showRelationships && (
        <>
          <DataTable
            columns={columns}
            data={rows}
            maxRows={pageSize}
            hideExport={false}
            hoverColor="green"
            equalWidthColumns={true}
            onRowClick={(row) => handleRowClick(row)}
          />

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, data.totalCount)} of {data.totalCount.toLocaleString()} records
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-auto rounded px-3 py-1.5 text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-muted-foreground text-sm px-3">
                Page {page} of {data.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
                className="h-auto rounded px-3 py-1.5 text-sm"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* No Data State */}
      {!loading && data && data.data.length === 0 && !showRelationships && (
        <div className="text-center py-12 text-muted-foreground">
          <Table2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg">No records found</p>
          <p className="text-sm mt-1">Try adjusting your filters or date range</p>
        </div>
      )}

      {/* Full Record Modal */}
      {fullRecordModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setFullRecordModal(null)}
        >
          <div
            className="relative w-full max-w-3xl max-h-screen-85 flex flex-col bg-popover border border-success/50 rounded-xl shadow-2xl shadow-success/20"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-3">
                <Code2 className="w-5 h-5 text-success" />
                <span className="text-white font-semibold text-sm">{fullRecordModal.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyRecordJson(fullRecordModal.record)}
                  className="h-auto gap-1.5 rounded px-3 py-1.5 text-xs font-medium"
                >
                  {copySuccess
                    ? <><Check className="w-3.5 h-3.5 text-success" /> Copied!</>
                    : <><Copy className="w-3.5 h-3.5" /> Copy JSON</>}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFullRecordModal(null)}
                  className="h-auto rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Scrollable JSON body */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Top-level scalar fields summary */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mb-4 text-xs">
                {Object.entries(fullRecordModal.record)
                  .filter(([, v]) => typeof v !== 'object' || v === null)
                  .map(([key, value]) => (
                    <div key={key} className="flex gap-2 min-w-0">
                      <span className="text-muted-foreground font-medium shrink-0">{key}:</span>
                      <span className="text-success break-all">{value === null ? 'null' : String(value)}</span>
                    </div>
                  ))}
              </div>

              {/* Payload / nested objects */}
              {Object.entries(fullRecordModal.record)
                .filter(([, v]) => typeof v === 'object' && v !== null)
                .map(([key, value]) => (
                  <div key={key} className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">{key}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <pre className="p-3 bg-background border border-card rounded-lg text-xs text-success overflow-x-auto whitespace-pre-wrap leading-relaxed">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
